/**
 * Validation utilities for various business and personal identification numbers
 */

/**
 * Validates an Australian Business Number (ABN)
 * An ABN is a unique 11-digit identifier issued to all entities registered in the Australian Business Register (ABR).
 * 
 * The algorithm:
 * 1. Subtract 1 from the first digit
 * 2. Multiply each of the digits by its weighting factor, based on its position in the ABN
 * 3. Sum the resulting products
 * 4. Divide the total by 89
 * 5. If the remainder is zero, the number is valid
 * 
 * Weighting factors: 10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19
 */
export function isValidABN(abn: string): boolean {
    if (!abn) return false;

    // Remove spaces and non-digit characters
    const cleanABN = abn.replace(/[^\d]/g, '');

    // Must be exactly 11 digits
    if (cleanABN.length !== 11) return false;

    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    let sum = 0;

    for (let i = 0; i < 11; i++) {
        let digit = parseInt(cleanABN[i]);

        // Step 1: Subtract 1 from the first digit
        if (i === 0) {
            digit -= 1;
        }

        // Step 2 & 3: Multiply by weight and add to sum
        sum += digit * weights[i];
    }

    // Step 4 & 5: Check if remainder is 0 when divided by 89
    return sum % 89 === 0;
}

/**
 * Validates an Australian Company Number (ACN)
 * An ACN is a unique nine-digit number issued by ASIC to every company registered in Australia.
 */
export function isValidACN(acn: string): boolean {
    if (!acn) return false;

    const cleanACN = acn.replace(/[^\d]/g, '');
    if (cleanACN.length !== 9) return false;

    const weights = [8, 7, 6, 5, 4, 3, 2, 1];
    let sum = 0;

    for (let i = 0; i < 8; i++) {
        sum += parseInt(cleanACN[i]) * weights[i];
    }

    const remainder = sum % 10;
    const checkDigit = (10 - remainder) % 10;

    return checkDigit === parseInt(cleanACN[8]);
}
